// The words the import screens say (release 3.2, universal vendor import).
//
// The API sends a plain label for everything a person reads: a field, an
// option, a layout, an outcome. These helpers only put those labels into
// sentences and pick the chip for a state, so no key or code the API uses to
// recognise an answer ever reaches the screen.

import { ApiError } from './api'
import { markErrorShown } from './feedback'
import type { StatusWord } from './status'

// The guarantee, said twice and nowhere else (release 3.3, the one promise
// rule): at Upload, before the file is chosen, and at Import, before the
// vendors are written. The API sends the same sentence as the banner once it
// has read a file.
export const IMPORT_BANNER = 'No emails or questionnaires are sent. Undo for 14 days.'

export const IMPORT_PROMISE = IMPORT_BANNER

// One or two words a step (the 7 Oct glossary).
export const IMPORT_STEPS = ['Upload', 'Match columns', 'Match values', 'Review and fix', 'Import']

// The two choices for a column that has no field of ours.
export const KEEP_EXTRA = ''
export const LEAVE_OUT = '-'
export const KEEP_EXTRA_LABEL = 'Keep as extra field'
export const LEAVE_OUT_LABEL = 'Do not import'

export function plural(n: number, one: string, many?: string): string {
  return n.toLocaleString('en-IN') + ' ' + (n === 1 ? one : (many ?? one + 's'))
}

// "Same PAN" reads "same PAN" in the middle of a sentence; an acronym at the
// start ("GSTIN matches") is left as it is.
export function lowerFirst(s: string): string {
  if (s.length < 2) return s.toLowerCase()
  const second = s.charAt(1)
  return second === second.toLowerCase() ? s.charAt(0).toLowerCase() + s.slice(1) : s
}

export function fileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' bytes'
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

// A large import is written in the background (the commit answers 202), and
// the page says so while it follows it.
export function importingWords(total: number): string {
  return 'Importing ' + plural(total, 'vendor') + '...'
}

// A commit that stopped part way, with nobody writing it any more (a restart
// of the server, say): how far it got. It can be resumed or undone.
export function stoppedWords(done: number, total: number): string {
  return 'Stopped at ' + done.toLocaleString('en-IN') + ' of ' + total.toLocaleString('en-IN') + ' rows.'
}

// A column's chip: Matched, Review or Unmatched, with an icon so the colour is
// never the only signal.
export const COLUMN_MATCHED: StatusWord = { word: 'Matched', tone: 'ok', icon: 'check' }
export const COLUMN_CHECK: StatusWord = { word: 'Review', tone: 'warn', icon: 'alert' }
export const COLUMN_NONE: StatusWord = { word: 'Unmatched', tone: 'neutral', icon: 'dot' }

// A row's chip on Review and fix. The word is the API's own label for the
// outcome; only the tone and the icon are chosen here.
export function outcomeWord(outcome: string, label: string): StatusWord {
  switch (outcome) {
    case 'ADD':
      return { word: label || 'New', tone: 'ok', icon: 'check' }
    case 'UPDATE':
      return { word: label || 'Update', tone: 'info', icon: 'pen' }
    case 'LEAVE':
      return { word: label || 'Skip', tone: 'plain', icon: 'dot' }
    case 'FIX':
      return { word: label || 'To fix', tone: 'warn', icon: 'alert' }
    case 'HELD':
      return { word: label || 'On hold', tone: 'neutral', icon: 'pause' }
    case 'SAME':
      return { word: label || 'Duplicate in file', tone: 'neutral', icon: 'flag' }
    default:
      return { word: label || 'Undecided', tone: 'neutral', icon: 'dot' }
  }
}

// A failure as it is said in place: the API's own words (a refused matching
// lists one sentence per problem) and what to do next. Marked as shown, so the
// console's feedback net does not say it a second time in a toast.
export function errorWords(err: unknown): string {
  markErrorShown(err)
  if (err instanceof ApiError) {
    const issues = (err.issues ?? []).map((i) => i.detail).filter((d) => !!d)
    // A refusal with no message of its own carries the request path in its
    // message ("/v1/imports/... returned 404."), which is never shown: the
    // issues are the words when there are any, and plain words otherwise.
    const own = err.message && !err.message.startsWith('/') ? err.message : plainFailure(err.status)
    const head = issues.length > 0 ? issues.join(' ') : own
    return head + (err.sentRemedy && !head.includes(err.sentRemedy) ? ' ' + err.sentRemedy : '')
  }
  const said = err instanceof Error ? err.message : String(err)
  return said + ' Try again in a moment.'
}

function plainFailure(status: number): string {
  if (status === 0) return 'We could not reach the server.'
  if (status === 403) return 'Your roles do not allow this.'
  if (status === 404) return 'We could not find this import any more.'
  if (status >= 500) return 'Something went wrong on our side.'
  return 'That did not work.'
}

// Whether support needs the code and reference of a failure: the server's own
// trouble, or no answer at all. A refusal the person can fix says how instead.
export function needsReference(err: unknown): boolean {
  if (!(err instanceof ApiError)) return true
  return err.status === 0 || err.status >= 500
}
